/**
 * Stock Management percentage display (tables, analysis, CSV export).
 * Always uses two decimal places for consistency across Holdings and Buy Recommendation tabs.
 */

import { formatSignedPercent, signedPercentClassName } from "./signedPercentFormat";

export const STOCK_PERCENT_FRACTION_DIGITS = 2;

const STOCK_PERCENT_LOCALE_OPTS = {
  minimumFractionDigits: STOCK_PERCENT_FRACTION_DIGITS,
  maximumFractionDigits: STOCK_PERCENT_FRACTION_DIGITS,
};

/**
 * Signed stock metric (e.g. %Change, 52W %/High) from a numeric DB value.
 * @param {unknown} value
 * @param {{ showPlus?: boolean }} [options]
 */
export function formatStockSignedPercent(value, { showPlus = true } = {}) {
  return formatSignedPercent(value, {
    showPlus,
    minFractionDigits: STOCK_PERCENT_FRACTION_DIGITS,
    maxFractionDigits: STOCK_PERCENT_FRACTION_DIGITS,
  });
}

/**
 * Parse a percent from Yahoo/Barchart table strings ("+5%", "-3.6", "N/A").
 * @returns {number|null}
 */
export function parseStockPercentValue(raw) {
  if (raw == null || raw === "") return null;
  const cleaned = String(raw).replace(/%/g, "").replace(/,/g, "").replace(/\+/g, "").trim();
  if (!cleaned || /^n\/a$/i.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Buy-recommendation / scraped string cells → signed display with tone.
 * @param {unknown} raw
 * @param {{ showPlus?: boolean }} [options]
 */
export function formatStockPercentCell(raw, { showPlus = true } = {}) {
  if (raw == null || raw === "") return { text: "—", tone: "empty" };
  const n = parseStockPercentValue(raw);
  if (n == null) {
    const trimmed = String(raw).trim();
    if (!trimmed) return { text: "—", tone: "empty" };
    return { text: trimmed, tone: "neutral" };
  }
  return formatStockSignedPercent(n, { showPlus });
}

/** Unsigned share (analysis breakdown tables, pie labels). */
export function formatStockPercentShare(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n.toLocaleString(undefined, STOCK_PERCENT_LOCALE_OPTS)}%`;
}

/** CSV export for holdings percent columns (no sign prefix). */
export function formatStockPctForDisplayExport(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${n.toLocaleString(undefined, { ...STOCK_PERCENT_LOCALE_OPTS, useGrouping: false })}%`;
}

export { signedPercentClassName };
